/**
 * The team a person accepted on /auth/onboard survives the round trip through
 * Auth0 under the `sessionStorage` key `heidi-onboarding:pending-team-join:v1`,
 * so the signed-in app can tell whether the join happened. The note stays until
 * the outcome is known and is then cleared, and logout clears it too.
 *
 * The key is repeated inline at each storage call below rather than held in a
 * constant, because `scripts/generate-persistence-manifest.ts` records raw Web
 * Storage keys only where it can read them as literals at the call site.
 */
export interface PendingTeamJoinNote {
  teamId: string;
  teamName: string;
  /** The account was created by this invite, as opposed to already existing. */
  isNewUser: boolean;
}

export const writePendingTeamJoinNote = (note: PendingTeamJoinNote): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;

  try {
    globalThis.sessionStorage.setItem(
      'heidi-onboarding:pending-team-join:v1',
      JSON.stringify(note)
    );
  } catch {
    // Storage may be unavailable (private mode) or full — the join still
    // happens, the app just cannot confirm it.
  }
};

export const clearPendingTeamJoinNote = (): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;

  try {
    globalThis.sessionStorage.removeItem('heidi-onboarding:pending-team-join:v1');
  } catch {
    // Nothing to recover from — the note expires with the tab regardless.
  }
};

const isNote = (value: unknown): value is PendingTeamJoinNote => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.teamId === 'string' &&
    candidate.teamId !== '' &&
    typeof candidate.teamName === 'string' &&
    typeof candidate.isNewUser === 'boolean'
  );
};

/** Reads the note and leaves it in storage; null when there is none or it is unreadable. */
export const readPendingTeamJoinNote = (): PendingTeamJoinNote | null => {
  if (typeof globalThis.sessionStorage === 'undefined') return null;

  let serialized: string | null = null;
  try {
    serialized = globalThis.sessionStorage.getItem(
      'heidi-onboarding:pending-team-join:v1'
    );
  } catch {
    return null;
  }
  if (!serialized) return null;

  try {
    const parsed: unknown = JSON.parse(serialized);
    return isNote(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
